/**
 * Policy record — Subjectivities tab (OR-1666, design OR-1639 "MVP").
 *
 * Two columns per the approved MVP: Required (the condition, with an
 * optional second line such as a deadline) and Status (a dropdown the
 * user changes in place). Per the design notes: no search box on this
 * tab, no "Owned by" column (it is always the broker) and no per-row
 * document download for the MVP.
 *
 * Statuses are the backend's: Satisfied green, Outstanding yellow, Waived
 * gray (the design's Received/Reopen were sample labels).
 *
 * States: the failed load ("Subjectivities couldn't be loaded") with Try
 * again, which re-requests the list.
 *
 * Reads `GET /policy-terms/{id}/subjectivities/` (backend OR-1620). The
 * status dropdown is optimistic, straight to
 * `PATCH /policy-terms/{id}/subjectivities/{attachmentId}`; a failed save
 * puts the previous status back.
 */
import { useEffect, useMemo, useState } from "react";
import { CheckDone01, Loading02, RefreshCw05 } from "@untitledui/icons";
import { Button, Select, Table, cx } from "@tricura/atlas-ui";
import { Skeleton } from "../../components/skeleton";
import { SortHeader, parseSort } from "../../components/table-sort-header";
import { type SubjectivityStatus, type TermSubjectivity, listTermSubjectivities, updateTermSubjectivityStatus } from "../../api";
import { TabState } from "./tab-state";

const SUBJECTIVITY_STATUSES: SubjectivityStatus[] = ["satisfied", "outstanding", "waived"];

const STATUS_META: Record<SubjectivityStatus, { label: string; dot: string }> = {
    satisfied: { label: "Satisfied", dot: "text-utility-green-500" },
    outstanding: { label: "Outstanding", dot: "text-utility-yellow-500" },
    waived: { label: "Waived", dot: "text-utility-neutral-500" },
};

function StatusDot({ status }: { status: SubjectivityStatus }) {
    return <span aria-hidden="true" className={cx("size-2 shrink-0 rounded-full bg-current", STATUS_META[status].dot)} />;
}

const STATUS_ITEMS = SUBJECTIVITY_STATUSES.map((s) => ({ id: s, label: STATUS_META[s].label, icon: <StatusDot status={s} /> }));

/** Owns its saving state: the table's row collection doesn't re-render on
 *  state held outside the rows. */
function StatusCell({ termId, row, onSaved }: { termId: string; row: TermSubjectivity; onSaved: (row: TermSubjectivity) => void }) {
    const [optimistic, setOptimistic] = useState<SubjectivityStatus | undefined>(undefined);
    const [pending, setPending] = useState(false);
    const value = optimistic ?? row.status;

    const apply = async (next: SubjectivityStatus) => {
        if (next === value) return;
        setOptimistic(next);
        setPending(true);
        try {
            onSaved(await updateTermSubjectivityStatus(termId, row.id, next));
        } catch (err) {
            console.error("Failed to update subjectivity status", err);
        } finally {
            setOptimistic(undefined);
            setPending(false);
        }
    };

    return (
        <div className="flex items-center justify-end gap-2">
            {pending && <Loading02 aria-label="Saving" className="size-4 text-fg-quaternary shrink-0 animate-spin" />}
            <Select
                size="sm"
                aria-label={`Status of ${row.title}`}
                selectedKey={value}
                items={STATUS_ITEMS}
                isDisabled={pending}
                className="w-36 sm:w-52"
                data-autotest="policy-subjectivities-select-status"
                onSelectionChange={(key) => {
                    if (key !== null) void apply(key as SubjectivityStatus);
                }}
            >
                {(item) => (
                    <Select.Item id={item.id} icon={item.icon}>
                        {item.label}
                    </Select.Item>
                )}
            </Select>
        </div>
    );
}

type Load = { status: "loading" } | { status: "ready"; rows: TermSubjectivity[] } | { status: "error" };

const SORT_VALUE: Record<string, (s: TermSubjectivity) => string | number> = {
    title: (s) => s.title.toLowerCase(),
    status: (s) => SUBJECTIVITY_STATUSES.indexOf(s.status),
};

export function SubjectivitiesTab({ termId }: { termId: string }) {
    const [load, setLoad] = useState<Load>({ status: "loading" });
    const [reloadKey, setReloadKey] = useState(0);
    const [sort, setSort] = useState<string | undefined>(undefined);

    /** Keeps the list (and the Status sort) on what the backend saved. */
    const replaceRow = (saved: TermSubjectivity) =>
        setLoad((prev) => (prev.status === "ready" ? { status: "ready", rows: prev.rows.map((r) => (r.id === saved.id ? saved : r)) } : prev));

    useEffect(() => {
        let cancelled = false;
        setLoad({ status: "loading" });
        listTermSubjectivities(termId)
            .then((rows) => !cancelled && setLoad({ status: "ready", rows }))
            .catch(() => !cancelled && setLoad({ status: "error" }));
        return () => {
            cancelled = true;
        };
    }, [termId, reloadKey]);

    const rows = useMemo(() => {
        const list = load.status === "ready" ? load.rows : [];
        const parsed = parseSort(sort);
        if (!parsed) return list;
        const value = SORT_VALUE[parsed.field];
        const dir = parsed.dir === "asc" ? 1 : -1;
        return [...list].sort((a, b) => (value(a) < value(b) ? -dir : value(a) > value(b) ? dir : 0));
    }, [load, sort]);

    if (load.status === "error") {
        return (
            <TabState
                icon={CheckDone01}
                title="Subjectivities couldn't be loaded"
                description={
                    <>
                        We couldn't load this policy's subjectivities list.
                        <br />
                        Open the original submission to review.
                    </>
                }
                data-autotest="policy-subjectivities-state-error"
                action={
                    <Button color="secondary" size="sm" iconTrailing={RefreshCw05} onClick={() => setReloadKey((k) => k + 1)}>
                        Try again
                    </Button>
                }
            />
        );
    }

    const skeletonItems = Array.from({ length: 6 }, (_, i) => ({ id: `skeleton-${i}` }));

    return (
        <Table aria-label="Subjectivities" data-autotest="policy-subjectivities-table" aria-busy={load.status === "loading" || undefined} wrapperClassName="atlas-scrollbar flex-1 min-h-0 overflow-auto">
            <Table.Header className="bg-secondary sticky top-0 z-20">
                <Table.Head id="title" isRowHeader>
                    <SortHeader field="title" sort={sort} onSortChange={setSort}>
                        Required
                    </SortHeader>
                </Table.Head>
                <Table.Head id="status" className="w-44 sm:w-60">
                    <SortHeader field="status" sort={sort} onSortChange={setSort}>
                        Status
                    </SortHeader>
                </Table.Head>
            </Table.Header>
            {load.status === "loading" ? (
                <Table.Body items={skeletonItems}>
                    {(item) => (
                        <Table.Row id={item.id}>
                            <Table.Cell>
                                <Skeleton className="h-4 w-64" />
                            </Table.Cell>
                            <Table.Cell>
                                <Skeleton className="ml-auto h-9 w-52" />
                            </Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            ) : (
                <Table.Body items={rows}>
                    {(row) => (
                        <Table.Row id={row.id} data-autotest="policy-subjectivities-row">
                            <Table.Cell>
                                <div className="flex min-w-0 flex-col">
                                    <span className={cx("text-sm", row.description ? "text-primary" : "text-secondary")}>{row.title}</span>
                                    {row.description && <span className="text-tertiary text-sm">{row.description}</span>}
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                <StatusCell termId={termId} row={row} onSaved={replaceRow} />
                            </Table.Cell>
                        </Table.Row>
                    )}
                </Table.Body>
            )}
        </Table>
    );
}
